import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ResolvedObjectAction } from "@/object-actions/types";
import { getActionIcon, getActionLabel } from "@/object-actions/utils";
import { cn } from "@/utils";

export interface ObjectActionIconButtonProps {
  action: ResolvedObjectAction;
  labelCtx: Parameters<typeof getActionLabel>[1];
  /** Selection bar styling — light icons on rust background. */
  variant?: "default" | "selection-bar" | "pane-header";
}

export function ObjectActionIconButton({
  action,
  labelCtx,
  variant = "default",
}: ObjectActionIconButtonProps) {
  const Icon = getActionIcon(action.definition, labelCtx);
  const label = getActionLabel(action.definition, labelCtx);

  if (variant === "pane-header") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="toolbarGhost"
            size="icon-sm"
            aria-label={label}
            disabled={action.disabled}
            onClick={() => window.setTimeout(() => action.handler(), 0)}
          >
            <Icon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          disabled={action.disabled}
          onClick={() => window.setTimeout(() => action.handler(), 0)}
          className={cn(
            "grid place-items-center h-7 w-7 rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed",
            variant === "selection-bar" &&
              "text-white/85 hover:text-white hover:bg-n-00/15",
            variant === "selection-bar" &&
              action.definition.destructive &&
              "hover:text-lc-blocked",
            variant === "default" &&
              "text-muted-foreground hover:text-foreground hover:bg-n-95/8 dark:hover:bg-n-95/10",
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
